import { useMemo, useState } from 'react';

import DateInput from 'components/field/DateInput';
import EnumSelect from 'components/field/EnumSelect';

import useDebounce from 'hooks/useDebounce';
import useUpdateEffect from 'hooks/useUpdateEffect';

import { FilterAction, FilterType } from './DynamicListFilterUtils';
import DynamicListFloatInput from './FilterInputs/DynamicListFloatInput';
import DynamicListMultiSelect from './FilterInputs/DynamicListMultiSelect';
import DynamicListNumberInput from './FilterInputs/DynamicListNumberInput';
import DynamicListStringInput from './FilterInputs/DynamicListStringInput';

const FilterActions = {
	string: FilterAction.startsWith,
	number: FilterAction.is,
	float: FilterAction.between,
	date: FilterAction.between,
	selection: FilterAction.is,
	multiSelection: FilterAction.is,
	enum: FilterAction.is,
	enumMulti: FilterAction.is
};

const HeaderFilter = (props) => {
	const { column, fetchUrl, filterModel, onFilterModelChange, multiSelectFilters, requestParameters } = props;
	const storedFilterObject = useMemo(
		() =>
			filterModel?.find(
				(filter) =>
					filter.column.accessor === column.accessor &&
					filter.action === (column.customHeaderFilterAction ?? FilterActions[column.filterType])
			),
		[filterModel, column]
	);
	const [selectedFilterValue, setSelectedFilterValue] = useState(storedFilterObject?.value ?? '');
	const debouncedFilterValue = useDebounce(selectedFilterValue, 600);
	const [correctedFilterValue, setCorrectedFilterValue] = useState(debouncedFilterValue);

	useUpdateEffect(() => {
		if (
			column.filterType === FilterType.string &&
			debouncedFilterValue?.trim().length > 0 &&
			debouncedFilterValue?.trim().length < 3
		)
			return;
		setCorrectedFilterValue(debouncedFilterValue);
	}, [debouncedFilterValue]);

	useUpdateEffect(() => {
		if (JSON.stringify(storedFilterObject?.value ?? '') === JSON.stringify(correctedFilterValue)) return;

		if (storedFilterObject) {
			setSelectedFilterValue(storedFilterObject.value);
		} else {
			setSelectedFilterValue('');
		}
	}, [storedFilterObject]);

	const addFilterObject = () => {
		const filterObject = {
			column,
			action: column.customHeaderFilterAction ?? FilterActions[column.filterType],
			value: selectedFilterValue
		};

		if (filterModel.length > 0) {
			let isThere = false;
			const filtersTemplate = filterModel.map((filter) => {
				if (filter.column.accessor === filterObject.column.accessor) {
					isThere = true;
					return filterObject;
				}

				return filter;
			});
			!isThere && filtersTemplate.push(filterObject);
			onFilterModelChange(filtersTemplate);
		} else {
			onFilterModelChange([filterObject]);
		}
	};

	const removeFilterObject = () => {
		const filtersTemplate = filterModel.filter((filter) => filter.column.accessor !== column.accessor);
		onFilterModelChange(filtersTemplate);
	};

	useUpdateEffect(() => {
		if (JSON.stringify(storedFilterObject?.value ?? '') === JSON.stringify(correctedFilterValue)) return;

		if (filterModel.length > 0) {
			const isEmptyValue = Array.isArray(correctedFilterValue)
				? correctedFilterValue.length === 0
				: correctedFilterValue === '' || correctedFilterValue === null || correctedFilterValue === undefined;

			if (isEmptyValue && storedFilterObject) {
				removeFilterObject();
			} else if (!isEmptyValue && storedFilterObject) {
				addFilterObject();
			} else if (!isEmptyValue && !storedFilterObject) {
				addFilterObject();
			}
		} else {
			addFilterObject();
		}
	}, [correctedFilterValue]);

	const renderFilterComponents = () => {
		switch (column.filterType) {
			case FilterType.string:
				return (
					<DynamicListStringInput
						autoFocus={false}
						className="header-filter"
						value={selectedFilterValue}
						setValue={(value) => setSelectedFilterValue(value ?? '')}
						tooltipTitle="headerFilter.min3CharactersRequired"
					/>
				);

			case FilterType.number:
				return (
					<DynamicListNumberInput
						autoFocus={false}
						className="header-filter"
						value={selectedFilterValue}
						setValue={(value) => setSelectedFilterValue(value ?? '')}
					/>
				);

			case FilterType.float:
				return (
					<DynamicListFloatInput
						autoFocus={false}
						className="header-filter"
						value={selectedFilterValue}
						setValue={setSelectedFilterValue}
					/>
				);

			case FilterType.date:
				return (
					<DateInput
						rangeMode
						className="header-filter"
						value={selectedFilterValue}
						setValue={setSelectedFilterValue}
					/>
				);

			case FilterType.selection:
				return (
					<DynamicListMultiSelect
						className="header-filter"
						accessor={column.accessor}
						fetchUrl={fetchUrl}
						value={selectedFilterValue}
						setValue={(value) => setSelectedFilterValue(value ?? '')}
						fixedContainerWidth={false}
						placeholder="select"
						labelPrefix={column.filterPrefix}
						filters={multiSelectFilters}
						requestParameters={requestParameters}
					/>
				);

			case FilterType.multiSelection:
				return (
					<DynamicListMultiSelect
						className="header-filter"
						accessor={column.accessor}
						fetchUrl={fetchUrl}
						value={selectedFilterValue}
						setValue={(value) => setSelectedFilterValue(value ?? '')}
						fixedContainerWidth={false}
						placeholder="select"
						multipleSelection
						labelPrefix={column.filterPrefix}
						filters={multiSelectFilters}
						requestParameters={requestParameters}
					/>
				);

			case FilterType.enum:
				return (
					<EnumSelect
						className="header-filter"
						placeholder="select"
						enumName={column.enumName}
						data={column.enumData}
						value={selectedFilterValue ?? null}
						setValue={(value) => setSelectedFilterValue(value ?? '')}
						fixedContainerWidth={false}
						labelPrefix={column.filterPrefix}
					/>
				);

			case FilterType.enumMulti:
				return (
					<EnumSelect
						className="header-filter"
						placeholder="select"
						enumName={column.enumName}
						data={column.enumData}
						value={selectedFilterValue ?? null}
						setValue={(value) => setSelectedFilterValue(value ?? '')}
						fixedContainerWidth={false}
						multipleSelection
						labelPrefix={column.filterPrefix}
					/>
				);

			default:
				return null;
		}
	};

	return <>{renderFilterComponents()}</>;
};

export default HeaderFilter;
